import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import SectionMessage from '@atlaskit/section-message';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { expVal } from '@confluence/feature-experiments';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

const i18n = defineMessages({
	body: {
		id: 'bulk-permissions.agent-access-warning.body',
		defaultMessage:
			'Adding an agent to all spaces makes all open content in the spaces accessible to anyone who can use the agent, even if they wouldn\u2019t have content access otherwise.',
		description:
			'Warning shown in bulk permissions when an AI agent is staged in the user picker, explaining the access implications',
	},
	dismiss: {
		id: 'bulk-permissions.agent-access-warning.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the dismiss button on the agent access warning',
	},
});

const STORAGE_SURFACE_KEY = 'bulk-permissions';
const AGENT_WARNING_DISMISSED_KEY = keys.AGENT_ACCESS_WARNING_DISMISSED;

type AgentAccessWarningProps = {
	stagedPrincipals: ReadonlyArray<{ appType?: string | null }>;
};

export const AgentAccessWarning = ({ stagedPrincipals }: AgentAccessWarningProps) => {
	const { formatMessage } = useIntl();

	const [isDismissed, setIsDismissed] = useState(() => {
		try {
			const dismissed = JSON.parse(localStorage.getItem(AGENT_WARNING_DISMISSED_KEY) ?? 'null');
			return STORAGE_SURFACE_KEY in (dismissed ?? {});
		} catch {
			return false;
		}
	});

	const handleDismiss = useCallback(() => {
		try {
			const existing =
				JSON.parse(localStorage.getItem(AGENT_WARNING_DISMISSED_KEY) ?? 'null') ?? {};
			localStorage.setItem(
				AGENT_WARNING_DISMISSED_KEY,
				JSON.stringify({ ...existing, [STORAGE_SURFACE_KEY]: 1 }),
			);
			setIsDismissed(true);
		} catch {
			return;
		}
	}, []);

	const hasAgent = stagedPrincipals.some((p) => p?.appType === 'agent');

	if (!hasAgent || isDismissed || !expVal('confluence_agent_access_warning', 'isEnabled', false)) {
		return null;
	}

	return (
		<Box xcss={styles.container}>
			<SectionMessage
				appearance="warning"
				testId="agent-access-warning-bulk-permissions"
			>
				<Box xcss={styles.body}>{formatMessage(i18n.body)}</Box>
			</SectionMessage>
			<Box xcss={styles.dismissButton}>
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.dismiss)}
					appearance="subtle"
					onClick={handleDismiss}
					testId="agent-access-warning-dismiss-bulk-permissions"
				/>
			</Box>
		</Box>
	);
};

const styles = cssMap({
	container: {
		position: 'relative',
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
	},
	body: {
		paddingRight: token('space.400'),
	},
	dismissButton: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
});
